import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Typography,
  Chip,
  Stack,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Assignment as RequisitionIcon,
} from '@mui/icons-material';
import PurchaseRequisitionService from '../../services/purchase-requisition.service';

const PurchaseRequisitionList: React.FC = () => {
  const navigate = useNavigate();
  const [requisitions, setRequisitions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRequisitions();
  }, []);

  const fetchRequisitions = async () => {
    try {
      setLoading(true);
      const response = await PurchaseRequisitionService.getAll({
        populate: ['requestedBy', 'department', 'approvedBy'],
        'pagination[pageSize]': 100,
        sort: 'requestDate:desc',
      });
      setRequisitions(response.data?.data || []);
    } catch (error) {
      console.error('Error fetching purchase requisitions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this requisition?')) {
      try {
        await PurchaseRequisitionService.delete(id);
        fetchRequisitions();
      } catch (error) {
        console.error('Error deleting requisition:', error);
      }
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved':
        return 'success';
      case 'ordered':
        return 'info';
      case 'completed':
        return 'primary';
      case 'pending':
        return 'warning';
      case 'rejected':
        return 'error';
      default:
        return 'default';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return 'error';
      case 'high':
        return 'warning';
      case 'medium':
        return 'info';
      case 'low':
        return 'default';
      default:
        return 'default';
    }
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">
          <RequisitionIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Purchase Requisitions
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/purchase-requisitions/create')}
        >
          New Requisition
        </Button>
      </Stack>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Requisition #</TableCell>
              <TableCell>Requested By</TableCell>
              <TableCell>Department</TableCell>
              <TableCell>Request Date</TableCell>
              <TableCell>Priority</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  Loading...
                </TableCell>
              </TableRow>
            ) : requisitions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  No purchase requisitions found
                </TableCell>
              </TableRow>
            ) : (
              requisitions.map((requisition) => {
                const attrs = requisition.attributes;
                const requestedBy = attrs?.requestedBy?.data?.attributes;
                const department = attrs?.department?.data?.attributes;

                return (
                  <TableRow key={requisition.id} hover>
                    <TableCell>
                      <Typography variant="body1" fontWeight="medium">
                        {attrs?.requisitionNumber}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {requestedBy?.firstName} {requestedBy?.lastName}
                    </TableCell>
                    <TableCell>{department?.name || '-'}</TableCell>
                    <TableCell>
                      {new Date(attrs?.requestDate).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={attrs?.priority || 'medium'}
                        color={getPriorityColor(attrs?.priority)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={attrs?.status || 'pending'}
                        color={getStatusColor(attrs?.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/purchase-requisitions/${requisition.id}/edit`)}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(requisition.id)}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default PurchaseRequisitionList;

